import PropTypes from 'prop-types'
import SearchCityResultPage from '@layouts/courses/city'
import Error from '@components/error'
import { QUERY_CITY_SEARCH } from '@components/course-search-results'
import { LIST_STATES_QUERY } from '@components/states'
import { fetchMultipleSSRData } from '@lib/ssr-fetch'
import { convertFromSlug } from '@utils/slugs'
import { firstQueryParam } from '@utils/firstQueryParam'
import states from '@config/states'
import initializeFeatures from '@lib/feature-flags/initializeFeatures'
import { isGolfFacilitiesDataSourceEnabled } from '@lib/feature-flags/facility-rollout'
import { loadPlayRouteData } from '@lib/golf-facilities/play-route-loader'
import { adaptFacilitySearchResultsToCourseCards } from '@lib/golf-facilities/adapters/city-courses'

const PLAY_CITY_LOG_PREFIX = '[play/city]'

export async function getServerSideProps({ req, res, query }) {
  const safeQuery = query ?? {}
  res.setHeader('Cache-Control', 'no-store')
  const { city, state } = safeQuery
  if (!city || !state) {
    return { props: { query: safeQuery } }
  }

  const featureFlags = await initializeFeatures({ req, res, query: safeQuery }).catch(() => null)

  if (!isGolfFacilitiesDataSourceEnabled(featureFlags)) {
    const convertedCity = convertFromSlug(city)
    const stateName = states[state.toUpperCase()]

    const result = await fetchMultipleSSRData({
      query: safeQuery,
      queries: [
        {
          name: 'courses',
          graphqlQuery: QUERY_CITY_SEARCH,
          variables: { city: convertedCity, stateName },
          clientName: 'pga-algolia',
          transform: (data) => data?.cityCourses,
        },
        {
          name: 'states',
          graphqlQuery: LIST_STATES_QUERY,
          variables: {},
          clientName: 'pga-algolia',
          transform: (data) => data?.listStates,
        },
      ],
    })
    return {
      props: {
        query: safeQuery,
        ssrCoursesData: result.courses ?? null,
        ssrStatesData: result.states ?? null,
      },
    }
  }

  const sdkCity = convertFromSlug(String(firstQueryParam(city)))
  const sdkState = String(firstQueryParam(state)).toUpperCase()

  const outcome = await loadPlayRouteData({
    call: (client) => client.facilities.search({ city: sdkCity, state: sdkState, includeAddress: true }),
    adapt: adaptFacilitySearchResultsToCourseCards,
    logPrefix: PLAY_CITY_LOG_PREFIX,
    includeStatesStrip: true,
  })
  if (outcome.notFound) return { notFound: true }

  return {
    props: {
      query: safeQuery,
      ssrCoursesData: outcome.data ?? [],
      ssrStatesData: outcome.statesData ?? [],
    },
  }
}

function CityCoursesPage(props) {
  const { query: { city, state } } = props
  return city && state ? <SearchCityResultPage {...props} /> : <Error statusCode={404} />
}

CityCoursesPage.propTypes = {
  query: PropTypes.object,
  ssrCoursesData: PropTypes.array,
  ssrStatesData: PropTypes.array,
}

export default CityCoursesPage
